/*
 * Copyright (c) 2023 Roboto Technologies, Inc.
 */
import { Box, CircularProgress, Switch, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import * as React from "react";

import { useAuth } from "@/providers";
import { RobotoTypography } from "@/shared/components";
import { UserRecord } from "@/shared/domain/users";
import { useLazyAPICall } from "@/shared/services/apiHooks";
import { APIService } from "@/shared/services/ApiService";
import { APIResponse, RobotoAPICall, usersEndpoint } from "@/types";

export const NotificationPreferences: React.FC = () => {
  const theme = useTheme();

  const { currentUser, currentOrganization } = useAuth();

  const [emailEnabled, setEmailEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  const { initiateRequest } = useLazyAPICall();

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);

      const apiCall: RobotoAPICall = {
        endpoint: usersEndpoint,
        method: "GET",
        orgId: currentOrganization?.org_id,
      };

      if (currentUser?.user_id) {
        const { response } =
          await APIService.authorizedRequest<APIResponse<UserRecord>>(apiCall);

        if (response?.data?.notification_channels_enabled?.email) {
          setEmailEnabled(true);
        }
      }

      setLoading(false);
    };

    void fetchUserData();
  }, [currentUser?.user_id, currentOrganization?.org_id]);

  return (
    <div>
      <RobotoTypography variant={"body1"} sx={{ marginBottom: "0.5rem" }}>
        Notification settings
      </RobotoTypography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: theme.spacing(1),
        }}
      >
        {loading && <CircularProgress size="1.5em" />}

        {!loading && (
          <>
            <Switch
              size="small"
              checked={emailEnabled}
              color="success"
              onChange={(e) => {
                setEmailEnabled(e.target.checked);

                void initiateRequest({
                  endpoint: usersEndpoint,
                  method: "PUT",
                  orgId: currentOrganization?.org_id,
                  requestBody: JSON.stringify({
                    notification_channels_enabled: {
                      email: e.target.checked,
                    },
                    notification_types_enabled: {
                      comment_mention: e.target.checked,
                    },
                  }),
                });
              }}
            />
            <RobotoTypography variant={"body2"}>
              Email notifications
            </RobotoTypography>
          </>
        )}
      </Box>
    </div>
  );
};
